import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';

interface AuditEvent {
  id: string;
  action: string;
  object_type: string | null;
  object_id: string | null;
  actor_email: string | null;
  actor_name: string | null;
  actor_role: string | null;
  ip: string | null;
  metadata: Record<string, unknown>;
  occurred_at: string;
}

/** Human labels for the action names the API records. */
const ACTION_LABEL: Record<string, string> = {
  'auth.login.success': 'Signed in',
  'auth.login.failure': 'Failed sign-in',
  'auth.login.locked': 'Account locked',
  'auth.logout': 'Signed out',
  'auth.signup': 'Workspace created',
  'driver.created': 'Driver added',
  'driver.updated': 'Driver updated',
  'driver.terminated': 'Driver terminated',
  'document.uploaded': 'Document filed',
  'document.downloaded': 'Document opened',
  'document.accepted': 'Submission accepted',
  'document.rejected': 'Submission rejected',
  'document.restricted_access': 'Restricted record accessed',
  'document.purged_by_retention': 'Destroyed at end of retention',
  'compliance.evaluated': 'File evaluated',
  'alert.sent': 'Alert sent',
  'alert.bounced': 'Alert bounced',
};

const FILTERS = [
  { value: '', label: 'Everything' },
  { value: 'document', label: 'Documents' },
  { value: 'driver', label: 'Drivers' },
  { value: 'auth', label: 'Sign-ins' },
  { value: 'alert', label: 'Alerts' },
];

export function AuditTrail() {
  const [action, setAction] = useState('');

  const { data, isPending, error } = useQuery({
    queryKey: ['audit', action],
    queryFn: () =>
      api.get<{ events: AuditEvent[]; nextBefore: number | null }>(
        `/api/audit?limit=100${action ? `&action=${action}` : ''}`,
      ),
  });

  if (isPending) return <p className="muted">Loading…</p>;
  if (error) return <p className="field-error">{(error as Error).message}</p>;

  const events = data?.events ?? [];

  return (
    <div className="fleet">
      <header className="fleet-head">
        <p className="eyebrow">Record of activity</p>
        <h1 className="fleet-title">Audit trail</h1>
        <p className="ledger">
          Who did what, and when. This record cannot be edited or deleted — not by your staff,
          and not by Milepost.
        </p>
      </header>

      <section className="card">
        <div className="card-header">
          <h2 className="fleet-table-title">
            Recent activity <span className="muted tnum">({events.length})</span>
          </h2>
          <div className="fleet-controls">
            <label className="sr-only" htmlFor="audit-filter">
              Filter by kind
            </label>
            <select id="audit-filter" value={action} onChange={(e) => setAction(e.target.value)}>
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {events.length === 0 ? (
          <p className="fleet-empty muted">Nothing recorded yet.</p>
        ) : (
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">What happened</th>
                  <th scope="col">Who</th>
                  <th scope="col">Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className="tnum muted" style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(e.occurred_at)}
                    </td>
                    <td>
                      <span className="headline-title">{ACTION_LABEL[e.action] ?? e.action}</span>
                      {e.metadata?.outcome === 'denied' && (
                        <span className="check-flag">denied</span>
                      )}
                    </td>
                    <td>
                      {e.actor_name ?? e.actor_email ?? <span className="muted">system</span>}
                      {e.actor_role && <span className="driver-id cite">{e.actor_role.replace('_', ' ')}</span>}
                    </td>
                    <td className="muted" style={{ fontSize: 'var(--fs-micro)' }}>
                      {describe(e.metadata)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Renders the metadata as a short phrase.
 *
 * Metadata never contains PII by policy — the audit trail describes actions, not data — so
 * this can print keys and values without redacting anything.
 */
function describe(metadata: Record<string, unknown>): string {
  const entries = Object.entries(metadata ?? {}).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return '—';
  return entries
    .map(([k, v]) => `${k.replace(/([A-Z])/g, ' $1').toLowerCase()}: ${String(v)}`)
    .join(' · ');
}
